import React, { useEffect, useState } from 'react';
import { billingAdminService, type BillingPlan } from '@/api/services/billingAdmin';
import { formatApiError } from '@/utils/formatApiError';
import { DataTableScrollArea } from '@/components/table';

const emptyPlan = (): Partial<BillingPlan> & { name: string; maxSearches: number } => ({
  name: '',
  slug: '',
  maxSearches: 5,
  priceCents: 0,
  currency: 'EUR',
  billingInterval: 'month',
  maxNotificationsPerDay: 50,
  minScrapeIntervalMinutes: 60,
  isActive: true,
});

function planToDraft(plan: BillingPlan): Partial<BillingPlan> & { name: string; maxSearches: number } {
  return {
    name: plan.name,
    slug: plan.slug,
    maxSearches: plan.maxSearches,
    priceCents: plan.priceCents,
    currency: plan.currency,
    billingInterval: plan.billingInterval,
    maxNotificationsPerDay: plan.maxNotificationsPerDay,
    minScrapeIntervalMinutes: plan.minScrapeIntervalMinutes,
    isActive: plan.isActive,
  };
}

export default function BillingPlansPage() {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [draft, setDraft] = useState(emptyPlan());
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<(Partial<BillingPlan> & { name: string; maxSearches: number }) | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await billingAdminService.listPlans(true);
      setPlans(res.items);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const slug = (draft.slug || draft.name).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      await billingAdminService.createPlan({ ...draft, slug, name: draft.name, maxSearches: draft.maxSearches });
      setDraft(emptyPlan());
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const openEdit = (plan: BillingPlan) => {
    setEditId(plan.id);
    setEditDraft(planToDraft(plan));
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId || !editDraft) return;
    try {
      await billingAdminService.updatePlan(editId, editDraft);
      setEditId(null);
      setEditDraft(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const toggleActive = async (plan: BillingPlan) => {
    try {
      await billingAdminService.updatePlan(plan.id, { isActive: !plan.isActive });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (loading) return <div className="panel">Loading billing packages…</div>;

  return (
    <div className="panel">
      <h1>Billing packages</h1>
      <p className="muted">Manual plans and quotas. Platform admin only.</p>
      {error ? <div className="alert alert-error">{error}</div> : null}

      <form className="form-grid" onSubmit={create}>
        <h2>Create package</h2>
        <label>
          Name
          <input
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label>
          Slug (optional)
          <input value={draft.slug ?? ''} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
        </label>
        <label>
          Max searches
          <input
            type="number"
            min={1}
            required
            value={draft.maxSearches}
            onChange={(e) => setDraft({ ...draft, maxSearches: Number(e.target.value) })}
          />
        </label>
        <label>
          Max notifications / day
          <input
            type="number"
            min={0}
            value={draft.maxNotificationsPerDay ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, maxNotificationsPerDay: Number(e.target.value) || null })
            }
          />
        </label>
        <label>
          Min scrape interval (minutes)
          <input
            type="number"
            min={0}
            value={draft.minScrapeIntervalMinutes ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, minScrapeIntervalMinutes: Number(e.target.value) || null })
            }
          />
        </label>
        <button type="submit" className="btn btn-primary">
          Create package
        </button>
      </form>

      <h2>Existing packages</h2>
      <DataTableScrollArea>
        <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Searches</th>
            <th>Notifications/day</th>
            <th>Scrape interval</th>
            <th>Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => (
            <tr key={plan.id}>
              <td>{plan.name}</td>
              <td>{plan.slug}</td>
              <td>{plan.maxSearches}</td>
              <td>{plan.maxNotificationsPerDay ?? '—'}</td>
              <td>{plan.minScrapeIntervalMinutes ?? '—'} min</td>
              <td>{plan.isActive ? 'yes' : 'no'}</td>
              <td className="table-action-bar">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(plan)}>
                  Edit
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleActive(plan)}>
                  {plan.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
        </DataTableScrollArea>

      {editId && editDraft ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setEditId(null)}>
          <form
            className="modal panel billing-plan-edit-modal"
            role="dialog"
            aria-labelledby="edit-plan-title"
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveEdit}
          >
            <h2 id="edit-plan-title">Edit package</h2>
            <label>
              Name
              <input
                required
                value={editDraft.name}
                onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
              />
            </label>
            <label>
              Max searches
              <input
                type="number"
                min={1}
                required
                value={editDraft.maxSearches}
                onChange={(e) => setEditDraft({ ...editDraft, maxSearches: Number(e.target.value) })}
              />
            </label>
            <label>
              Max notifications / day
              <input
                type="number"
                min={0}
                value={editDraft.maxNotificationsPerDay ?? ''}
                onChange={(e) =>
                  setEditDraft({
                    ...editDraft,
                    maxNotificationsPerDay: Number(e.target.value) || null,
                  })
                }
              />
            </label>
            <label>
              Min scrape interval (minutes)
              <input
                type="number"
                min={0}
                value={editDraft.minScrapeIntervalMinutes ?? ''}
                onChange={(e) =>
                  setEditDraft({
                    ...editDraft,
                    minScrapeIntervalMinutes: Number(e.target.value) || null,
                  })
                }
              />
            </label>
            <div className="table-action-bar">
              <button type="submit" className="btn btn-primary">
                Save changes
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setEditId(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
